import React, { useState, useEffect, useCallback, useRef } from 'react';
import socketService from '../services/socket';
import SERVER_CONFIG from '../services/config';
import '../styles/PetsCheckinPage.css';
import CameraModal from '../components/CameraModal';

const API_URL = SERVER_CONFIG.apiUrl;

function getToken() { return localStorage.getItem('token'); }
function authHeaders() { return { Authorization: 'Bearer ' + getToken(), 'Content-Type': 'application/json' }; }

const TYPE_EMOJI = { 'кот': '🐱', 'собака': '🐶' };
const TYPE_LABEL = { 'кот': 'Кот', 'собака': 'Собака' };

// ─── Модалка перемещения ──────────────────────────────────────────────────────
function MoveModal({ animal, locations, statuses, onSave, onClose }) {
  const [locId, setLocId] = useState(animal.location_id != null ? String(animal.location_id) : '');
  const [status, setStatus] = useState('в_приюте');
  const [saving, setSaving] = useState(false);

  async function handleSave() {
    setSaving(true);
    try {
      await fetch(`${API_URL}/pets-checkin/animals/${animal.id}/move`, {
        method: 'PUT',
        headers: authHeaders(),
        body: JSON.stringify({ location_id: locId ? parseInt(locId) : null, status }),
      });
      onSave({ location_id: locId ? parseInt(locId) : null, status });
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pc-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pc-modal">
        <div className="pc-modal-title">Переместить животное</div>
        <div className="pc-modal-animal">
          {TYPE_EMOJI[animal.type] || '🐾'} {animal.name} · {TYPE_LABEL[animal.type] || animal.type}
        </div>

        <div className="pc-field">
          <label className="pc-label">Локация</label>
          <select className="pc-select" value={locId} onChange={e => setLocId(e.target.value)}>
            <option value="">— Без локации —</option>
            {locations.map(l => <option key={l.id} value={l.id}>{l.name}</option>)}
          </select>
        </div>

        <div className="pc-field">
          <label className="pc-label">Статус</label>
          <select className="pc-select" value={status} onChange={e => setStatus(e.target.value)}>
            {statuses.map(s => <option key={s.value} value={s.value}>{s.label}</option>)}
          </select>
        </div>

        <div className="pc-modal-actions">
          <button className="pc-btn-cancel" onClick={onClose}>Отмена</button>
          <button className="pc-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение…' : 'Сохранить'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Модалка добавления животного ────────────────────────────────────────────
function AddAnimalModal({ locationId, locationName, onSave, onClose, initialName = '' }) {
  const [form, setForm] = useState({ name: initialName, type: 'кот', gender: 'неизвестно', age_years: '', age_months: '' });
  const [photo, setPhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [showCamera, setShowCamera] = useState(false);
  const fileRef = useRef();

  function set(field, val) { setForm(f => ({ ...f, [field]: val })); }

  function onPhotoChange(e) {
    const file = e.target.files[0];
    if (!file) return;
    setPhoto(file);
    setPhotoPreview(URL.createObjectURL(file));
  }

  async function handleSave() {
    if (!form.name.trim()) return setError('Введите кличку');
    if (!photo) return setError('Добавьте фото');
    setSaving(true);
    setError('');
    try {
      const fd = new FormData();
      fd.append('name', form.name.trim());
      fd.append('type', form.type);
      fd.append('gender', form.gender);
      if (form.age_years) fd.append('age_years', form.age_years);
      if (form.age_months) fd.append('age_months', form.age_months);
      if (locationId != null) fd.append('location_id', locationId);
      fd.append('photo', photo);

      const res = await fetch(`${API_URL}/pets-checkin/animals`, {
        method: 'POST',
        headers: { Authorization: 'Bearer ' + getToken() },
        body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || data.error || 'Ошибка');
      onSave(data);
    } catch (e) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="pc-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
      <div className="pc-modal">
        <div className="pc-modal-title">Новое животное</div>
        {locationName && (
          <div className="pc-modal-animal">📍 Локация: {locationName}</div>
        )}

        <div className="pc-field">
          <label className="pc-label">Кличка <span className="pc-required">*</span></label>
          <input className="pc-input" value={form.name} onChange={e => set('name', e.target.value)} placeholder="Введите кличку" />
        </div>

        <div className="pc-field">
          <label className="pc-label">Тип <span className="pc-required">*</span></label>
          <select className="pc-select" value={form.type} onChange={e => set('type', e.target.value)}>
            <option value="кот">Кот</option>
            <option value="собака">Собака</option>
          </select>
        </div>

        <div className="pc-field">
          <label className="pc-label">Пол <span className="pc-required">*</span></label>
          <select className="pc-select" value={form.gender} onChange={e => set('gender', e.target.value)}>
            <option value="М">Мужской</option>
            <option value="Ж">Женский</option>
            <option value="неизвестно">Неизвестно</option>
          </select>
        </div>

        <div className="pc-field" style={{ display: 'flex', gap: 8 }}>
          <div style={{ flex: 1 }}>
            <label className="pc-label">Лет</label>
            <input className="pc-input" type="number" min="0" max="30" value={form.age_years}
              onChange={e => set('age_years', e.target.value)} placeholder="0" />
          </div>
          <div style={{ flex: 1 }}>
            <label className="pc-label">Месяцев</label>
            <input className="pc-input" type="number" min="0" max="11" value={form.age_months}
              onChange={e => set('age_months', e.target.value)} placeholder="0" />
          </div>
        </div>

        <div className="pc-field">
          <label className="pc-label">Фото <span className="pc-required">*</span></label>
          {photoPreview && <img src={photoPreview} alt="" className="pc-photo-preview" />}
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <label className="pc-photo-btn">
              {photo ? '🖼 Изменить фото' : '🖼 Из галереи'}
              <input type="file" accept="image/*" style={{ display: 'none' }} ref={fileRef} onChange={onPhotoChange} />
            </label>
            <button type="button" className="pc-photo-btn" onClick={() => setShowCamera(true)}>
              📷 Сфотографировать
            </button>
          </div>
        </div>
        {showCamera && (
          <CameraModal
            isOpen={true}
            photoOnly={true}
            onCapture={items => {
              if (items && items.length > 0) {
                setPhoto(items[0].file);
                setPhotoPreview(items[0].preview);
              }
              setShowCamera(false);
            }}
            onClose={() => setShowCamera(false)}
          />
        )}

        {error && <div style={{ color: '#e53935', fontSize: 13, marginBottom: 8 }}>{error}</div>}

        <div className="pc-modal-actions">
          <button className="pc-btn-cancel" onClick={onClose}>Отмена</button>
          <button className="pc-btn-save" onClick={handleSave} disabled={saving}>
            {saving ? 'Сохранение…' : 'Добавить'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── Поиск в локации ──────────────────────────────────────────────────────────
function LocationSearch({ locationId, locationName, onAnimalAdded }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [showAdd, setShowAdd] = useState(false);
  const [open, setOpen] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    if (!query.trim()) { setResults([]); setOpen(false); return; }
    clearTimeout(timer.current);
    let cancelled = false;
    timer.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/pets-checkin/search?q=${encodeURIComponent(query)}`,
          { headers: authHeaders() });
        const data = await res.json();
        if (!cancelled) { setResults(data); setOpen(true); }
      } catch {}
    }, 300);
    return () => { cancelled = true; };
  }, [query]);

  function handleSelect(animal) {
    setQuery('');
    setOpen(false);
    // Уведомляем родителя — животное уже есть в базе
    onAnimalAdded(animal);
  }

  return (
    <div className="pc-search-wrap">
      <input
        className="pc-search-input"
        value={query}
        onChange={e => setQuery(e.target.value)}
        onFocus={() => query && setOpen(true)}
        placeholder="Добавить животное | Напишите кличку"
      />
      {open && (
        <div className="pc-search-results">
          {results.map(a => (
            <div key={a.id} className="pc-search-item" onClick={() => handleSelect(a)}>
              {a.photo_url
                ? <img src={a.photo_url} alt="" className="pc-search-item-photo" />
                : <div className="pc-search-no-photo">{TYPE_EMOJI[a.type] || '🐾'}</div>
              }
              <div className="pc-search-item-info">
                <div className="pc-search-item-name">{a.name}</div>
                <div className="pc-search-item-sub">{TYPE_LABEL[a.type] || a.type} · {a.location_name || 'Без локации'}</div>
              </div>
            </div>
          ))}
          <div className="pc-search-item pc-search-add" onClick={() => { setOpen(false); setShowAdd(true); }}>
            ＋ Добавить в базу новое животное
          </div>
        </div>
      )}

      {showAdd && (
        <AddAnimalModal
          initialName={query}
          locationId={locationId}
          locationName={locationName}
          onSave={animal => { setShowAdd(false); onAnimalAdded(animal); }}
          onClose={() => setShowAdd(false)}
        />
      )}
    </div>
  );
}

// ─── Секция локации ───────────────────────────────────────────────────────────
function LocationSection({ section, onToggle, onConfirm, onMoveOpen, onAnimalAdded, onPhotoClick, highlightedAnimalId }) {
  const total = section.animals.length;
  const checked = section.animals.filter(a => a.checked).length;
  const confirmed = section.confirmed;
  const progress = total > 0 ? (checked / total) * 100 : 0;

  return (
    <div className="pc-section">
      <div className={`pc-section-header${confirmed ? ' confirmed' : ''}`}>
        <div className="pc-section-progress" style={{ width: progress + '%' }} />
        <span className="pc-section-name">{section.name}</span>
        <span className="pc-section-count">
          {checked}/{total}
          {confirmed && <span className="pc-confirmed-badge" style={{ marginLeft: 8 }}>✓ Подтверждено</span>}
        </span>
      </div>

      {total === 0 ? (
        <div style={{ padding: '10px 14px', color: '#aaa', fontSize: 13 }}>Нет животных</div>
      ) : (
        <table className="pc-table">
          <thead>
            <tr>
              <th className="col-photo">Фото</th>
              <th className="col-name">Кличка</th>
              <th className="col-type">Тип</th>
              <th className="col-check">✓</th>
              <th className="col-move"></th>
            </tr>
          </thead>
          <tbody>
            {section.animals.map(animal => (
              <tr
                key={animal.id}
                data-animal-id={animal.id}
                className={`${animal.checked ? 'row-checked' : ''}${animal.id === highlightedAnimalId ? ' row-highlight' : ''}`}
              >
                <td className="col-photo">
                  {animal.photo_url
                    ? <img src={animal.photo_url} alt={animal.name} className="pc-photo" style={{ cursor: "pointer" }} onClick={() => onPhotoClick(animal.photo_url)} />
                    : <div className="pc-no-photo">{TYPE_EMOJI[animal.type] || '🐾'}</div>
                  }
                </td>
                <td className="col-name" style={{ fontWeight: 500 }}>{animal.name}</td>
                <td className="col-type">{TYPE_LABEL[animal.type] || animal.type}</td>
                <td className="col-check">
                  <input
                    type="checkbox"
                    className="pc-checkbox"
                    checked={!!animal.checked}
                    onChange={e => onToggle(animal, e.target.checked)}
                  />
                </td>
                <td className="col-move">
                  <button className="pc-move-btn" onClick={() => onMoveOpen(animal)} title="Переместить">
                    ↗
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <LocationSearch
        locationId={section.id}
        locationName={section.name}
        onAnimalAdded={animal => onAnimalAdded(section.id, animal)}
      />

      {!confirmed && (
        <div className="pc-confirm-wrap">
          <button
            className="pc-confirm-btn"
            disabled={checked < total || total === 0}
            onClick={() => onConfirm(section.id)}
          >
            {checked < total
              ? `Подтвердить (отмечено ${checked} из ${total})`
              : 'Подтвердить локацию ✓'}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Главный компонент ────────────────────────────────────────────────────────
export default function PetsCheckinPage({ user }) {
  const [sections, setSections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ locations: [], statuses: [] });
  const [moveAnimal, setMoveAnimal] = useState(null);
  const [fullscreenPhoto, setFullscreenPhoto] = useState(null);
  const [highlightedAnimalId, setHighlightedAnimalId] = useState(null);

  const today = new Date().toLocaleDateString('ru-RU', { day: 'numeric', month: 'long', year: 'numeric' });

  const load = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [dataRes, metaRes] = await Promise.all([
        fetch(`${API_URL}/pets-checkin/data`, { headers: authHeaders() }),
        fetch(`${API_URL}/pets-checkin/meta`,  { headers: authHeaders() }),
      ]);
      const [data, metaData] = await Promise.all([dataRes.json(), metaRes.json()]);
      setSections(Array.isArray(data) ? data : []);
      setMeta(metaData);
    } catch (e) {
      console.error('[PetsCheckin] load error:', e);
    } finally {
      if (!silent) setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Socket real-time
  useEffect(() => {
    function onToggled({ animal_id, location_id, checked }) {
      setSections(prev => prev.map(sec => ({
        ...sec,
        animals: sec.animals.map(a => a.id === animal_id ? { ...a, checked } : a),
        confirmed: checked === false && sec.id === location_id ? false : sec.confirmed,
      })));
    }

    function onConfirmed({ location_id }) {
      setSections(prev => prev.map(sec =>
        sec.id === location_id ? { ...sec, confirmed: true } : sec
      ));
    }

    function onMoved({ animal_id, location_id, status }) {
      load({ silent: true });
    }

    socketService.on('checkin:toggled',      onToggled);
    socketService.on('checkin:confirmed',    onConfirmed);
    socketService.on('checkin:animal_moved', onMoved);

    return () => {
      socketService.off('checkin:toggled');
      socketService.off('checkin:confirmed');
      socketService.off('checkin:animal_moved');
    };
  }, [load]);

  // Автопрокрутка к выбранному в поиске животному
  useEffect(() => {
    if (highlightedAnimalId == null) return;
    const timers = [60, 200, 500].map(delay => setTimeout(() => {
      const el = document.querySelector(`tr[data-animal-id="${highlightedAnimalId}"]`);
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }, delay));
    const clearHl = setTimeout(() => setHighlightedAnimalId(null), 2500);
    return () => { timers.forEach(clearTimeout); clearTimeout(clearHl); };
  }, [highlightedAnimalId]);

  async function handleToggle(animal, checked) {
    // Оптимистичное обновление
    setSections(prev => prev.map(sec => ({
      ...sec,
      animals: sec.animals.map(a => a.id === animal.id ? { ...a, checked } : a),
      confirmed: !checked && sec.id === animal.location_id ? false : sec.confirmed,
    })));

    try {
      await fetch(`${API_URL}/pets-checkin/toggle`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ animal_id: animal.id, location_id: animal.location_id, checked }),
      });
    } catch {
      load(); // откат при ошибке
    }
  }

  async function handleConfirm(locationId) {
    setSections(prev => prev.map(sec =>
      sec.id === locationId ? { ...sec, confirmed: true } : sec
    ));
    try {
      await fetch(`${API_URL}/pets-checkin/confirm`, {
        method: 'POST',
        headers: authHeaders(),
        body: JSON.stringify({ location_id: locationId }),
      });
    } catch {
      load();
    }
  }

  function handleMoveOpen(animal) { setMoveAnimal(animal); }

  async function handleMoveSave(update) {
    setMoveAnimal(null);
    load({ silent: true });
  }

  function handleAnimalAdded(locationId, animal) {
    load({ silent: true });
    if (animal && animal.id != null) setHighlightedAnimalId(animal.id);
  }

  return (
    <div className="pets-checkin-page">
      <div className="pc-header">
        <div>
          <div className="pc-title">🐾 Перекличка</div>
          <div className="pc-date">{today}</div>
        </div>
        <button className="pc-refresh" onClick={load} title="Обновить">↻</button>
      </div>

      {loading ? (
        <div className="pc-loading">Загрузка…</div>
      ) : sections.length === 0 ? (
        <div className="pc-empty">Нет данных о животных</div>
      ) : (
        sections.map(sec => (
          <LocationSection
            key={sec.id ?? 'no-loc'}
            section={sec}
            onToggle={handleToggle}
            onConfirm={handleConfirm}
            onMoveOpen={handleMoveOpen}
            onAnimalAdded={handleAnimalAdded}
            onPhotoClick={setFullscreenPhoto}
            highlightedAnimalId={highlightedAnimalId}
          />
        ))
      )}

      {fullscreenPhoto && (
        <div
          onClick={() => setFullscreenPhoto(null)}
          style={{
            position: 'fixed', inset: 0, zIndex: 9999,
            background: 'rgba(0,0,0,0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}
        >
          <img
            src={fullscreenPhoto}
            alt=""
            style={{ maxWidth: '100vw', maxHeight: '100vh', objectFit: 'contain' }}
          />
        </div>
      )}

      {moveAnimal && (
        <MoveModal
          animal={moveAnimal}
          locations={meta.locations}
          statuses={meta.statuses}
          onSave={handleMoveSave}
          onClose={() => setMoveAnimal(null)}
        />
      )}
    </div>
  );
}
